/** Feature-based admin permissions (synced with backend catalog). */

export const ADMIN_FEATURE_ROUTE_MAP: Record<string, string | null> = {
  dashboard: '/admin/dashboard',
  users: '/admin/users',
  properties: '/admin/properties',
  contracts: '/admin/contracts',
  policies: '/admin/policies',
  hierarchy: '/admin/hierarchy',
  admin_users: '/admin/hierarchy',
  tasks: '/admin/users',
  reports: '/admin/users',
  complaints: '/admin/users',
  issues: '/admin/users',
  support: '/admin/users',
  kyc: '/admin/kyc',
  roles: '/admin/roles',
  advertising: '/admin/advertising-management',
  store: '/admin/store-items',
  app_downloads: '/admin/app-downloads',
};

export interface AdminSessionCapabilities {
  isSuperAdmin: boolean;
  hasFullAccess: boolean;
  isDefault: boolean;
  adminLevel: number;
  hierarchyLevel: number;
  roleCode: string;
  permissionNames: string[];
  featureKeys: string[];
}

export function loadAdminCapabilities(): AdminSessionCapabilities {
  const isSuperAdmin = localStorage.getItem('admin_is_super_admin') === 'true';
  const hasFullAccess = localStorage.getItem('admin_has_full_access') === 'true';
  const isDefault = localStorage.getItem('admin_is_default') === 'true';
  const adminLevel = parseInt(localStorage.getItem('admin_level') || '99', 10);
  const hierarchyLevel = parseInt(
    localStorage.getItem('admin_hierarchy_level') || String(adminLevel),
    10
  );
  const roleCode = localStorage.getItem('admin_role_code') || '';
  let permissionNames: string[] = [];
  let featureKeys: string[] = [];
  try {
    permissionNames = JSON.parse(localStorage.getItem('admin_permission_names') || '[]');
    featureKeys = JSON.parse(localStorage.getItem('admin_feature_keys') || '[]');
  } catch {
    permissionNames = [];
    featureKeys = [];
  }
  return {
    isSuperAdmin,
    hasFullAccess,
    isDefault,
    adminLevel,
    hierarchyLevel,
    roleCode,
    permissionNames,
    featureKeys,
  };
}

export function saveAdminCapabilities(data: {
  is_super_admin?: boolean;
  has_full_access?: boolean;
  is_default?: boolean;
  admin_level?: number;
  hierarchy_level?: number;
  role_code?: string;
  permission_names?: string[];
  feature_keys?: string[];
}) {
  if (data.is_super_admin !== undefined) {
    localStorage.setItem('admin_is_super_admin', data.is_super_admin ? 'true' : 'false');
  }
  if (data.has_full_access !== undefined) {
    localStorage.setItem('admin_has_full_access', data.has_full_access ? 'true' : 'false');
  }
  if (data.is_default !== undefined) {
    localStorage.setItem('admin_is_default', data.is_default ? 'true' : 'false');
  }
  if (data.admin_level !== undefined) {
    localStorage.setItem('admin_level', String(data.admin_level));
  }
  if (data.hierarchy_level !== undefined) {
    localStorage.setItem('admin_hierarchy_level', String(data.hierarchy_level));
  }
  if (data.role_code) {
    localStorage.setItem('admin_role_code', data.role_code);
  }
  if (data.permission_names) {
    localStorage.setItem('admin_permission_names', JSON.stringify(data.permission_names));
  }
  if (data.feature_keys) {
    localStorage.setItem('admin_feature_keys', JSON.stringify(data.feature_keys));
  }
}

export function clearAdminCapabilities() {
  localStorage.removeItem('admin_is_super_admin');
  localStorage.removeItem('admin_has_full_access');
  localStorage.removeItem('admin_is_default');
  localStorage.removeItem('admin_hierarchy_level');
  localStorage.removeItem('admin_role_code');
  localStorage.removeItem('admin_permission_names');
  localStorage.removeItem('admin_feature_keys');
}

export function hasFeature(caps: AdminSessionCapabilities, featureKey: string): boolean {
  if (caps.hasFullAccess || caps.isSuperAdmin || caps.isDefault) return true;
  if (caps.featureKeys.includes(featureKey)) return true;
  return false;
}

export function canAccessAdminRoute(path: string, caps: AdminSessionCapabilities): boolean {
  if (caps.hasFullAccess || caps.isSuperAdmin) return true;

  const base = path.split('?')[0];

  if (base === '/admin/dashboard' || base === '/admin' || base === '/home') {
    return true;
  }

  if (base === '/admin/users') {
    return (
      hasFeature(caps, 'users') ||
      hasFeature(caps, 'kyc') ||
      hasFeature(caps, 'tasks') ||
      hasFeature(caps, 'reports') ||
      hasFeature(caps, 'complaints') ||
      hasFeature(caps, 'issues') ||
      hasFeature(caps, 'support') ||
      hasFeature(caps, 'history') ||
      hasFeature(caps, 'transactions')
    );
  }

  for (const [featureKey, route] of Object.entries(ADMIN_FEATURE_ROUTE_MAP)) {
    if (!route) continue;
    if (base === route || base.startsWith(`${route}/`)) {
      return hasFeature(caps, featureKey);
    }
  }

  if (base.startsWith('/admin/hierarchy')) {
    return (
      hasFeature(caps, 'hierarchy') ||
      hasFeature(caps, 'admin_users')
    );
  }

  if (base.startsWith('/admin/kyc')) {
    return hasFeature(caps, 'kyc');
  }

  return false;
}

export function getAdminRoleDisplayName(roleName?: string | null): string {
  if (!roleName) return 'N/A';
  const normalized = roleName.toLowerCase();
  if (normalized.includes('super')) return 'Super Admin';
  if (normalized.includes('manager')) return 'Senior';
  if (normalized.includes('operator')) return 'Admin cấp 3';
  if (normalized.includes('viewer')) return 'CTV';
  return roleName;
}

export function isAdminHeader(caps: AdminSessionCapabilities): boolean {
  return caps.hasFullAccess || caps.isSuperAdmin || caps.isDefault || caps.roleCode === 'SUPER_ADMIN';
}

export function canManageAdminUsers(caps: AdminSessionCapabilities): boolean {
  return hasFeature(caps, 'admin_users');
}

export function canManageTasks(caps: AdminSessionCapabilities): boolean {
  return hasFeature(caps, 'tasks');
}

export function canManageReports(caps: AdminSessionCapabilities): boolean {
  return hasFeature(caps, 'reports');
}

export function canManageComplaints(caps: AdminSessionCapabilities): boolean {
  return hasFeature(caps, 'complaints');
}

export function canManageIssues(caps: AdminSessionCapabilities): boolean {
  return hasFeature(caps, 'issues');
}

export function canManageSupport(caps: AdminSessionCapabilities): boolean {
  return hasFeature(caps, 'support');
}

export function canManageKyc(caps: AdminSessionCapabilities): boolean {
  return hasFeature(caps, 'kyc');
}

export function canManageRoles(caps: AdminSessionCapabilities): boolean {
  return hasFeature(caps, 'roles');
}
